/**
 * Sawyer 08 meds-feeding (G3/A1) — "Edit meds & feeding": the staff sheet that
 * puts a dog's medications, medication notes and feeding into the app.
 *
 * Sawyer (2026-10-23) wants one list of every in-house dog's meds and
 * feeding by time of day; that list is only as good as what staff can put
 * in, and until now only the pet parent could add a medication. This sheet
 * is opened from the Pet Profile, the Meds & feeding report and the med
 * pass (so a kennel tech, who cannot open the profile, can still fix a dose
 * from the floor — refuter C-8).
 *
 * Each section saves on its own (one request per action), so a typo in the
 * feeding box can never cost a medication change.
 *
 * DESIGN-PHILOSOPHY: #2 one obvious next action per section (one primary
 * button each, the sheet has no global Save); #5 never lie (a medication
 * with dose records cannot be renamed or deleted, and the sheet says why and
 * offers the honest alternative); #7 built for hands (≥44px chips and
 * fields, bottom sheet under 768px). Five states: default, focus, saving
 * ("Saving…", fields disabled), empty ("No medications yet…"), error (the
 * server's sentence under the field, or a banner with the recovery).
 */
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  createMedication,
  deleteMedication,
  fieldIssues,
  stopMedication,
  updateFeeding,
  updateMedication,
  type FieldIssue,
  type MedicationInput,
  type SlotSource,
} from '../../lib/meds-feeding-api.js';
import { MEAL_LABEL, MEAL_ORDER, SLOT_ORDER, SLOT_SHORT } from '../../lib/care-labels.js';
import { medTimesText } from './med-line.js';
import './meds-feeding.css';

export interface EditorMedication {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  instructions: string | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  slots?: string[];
  effectiveSlots?: string[];
  slotSource?: SlotSource;
}

export interface EditorDog {
  id: string;
  name: string;
  medications: EditorMedication[];
  medicationNotes: string | null;
  feedingNotes: string | null;
  foodType: string | null;
  feedingMethod: string | null;
  feedingSlots?: string[];
}

export interface MedsFeedingEditorProps {
  apiUrl: string;
  dog: EditorDog;
  onClose: () => void;
  /** Called after every successful save so the caller can refetch. */
  onChanged: () => void;
  /** A line under the title, e.g. "Past medications are on the dog profile." */
  footnote?: string;
}

const FREQUENCIES: ReadonlyArray<[string, string]> = [
  ['', 'Not set'],
  ['once_daily', 'Once daily'],
  ['twice_daily', 'Twice daily'],
  ['weekly', 'Weekly'],
  ['monthly', 'Monthly'],
  ['as_needed', 'As needed'],
];
const FOOD_TYPES: ReadonlyArray<[string, string]> = [
  ['', 'Not set'],
  ['dry', 'Dry'],
  ['wet', 'Wet'],
  ['raw', 'Raw'],
  ['mixed', 'Mixed'],
];
const METHODS: ReadonlyArray<[string, string]> = [
  ['', 'Not set'],
  ['free_feed', 'Free feed'],
  ['scheduled', 'Scheduled'],
  ['measured', 'Measured portions'],
];

interface MedDraft {
  name: string;
  dosage: string;
  frequency: string;
  instructions: string;
  startDate: string;
  endDate: string;
  slots: string[];
}

const EMPTY_DRAFT: MedDraft = {
  name: '',
  dosage: '',
  frequency: '',
  instructions: '',
  startDate: '',
  endDate: '',
  slots: [],
};

function draftFrom(m: EditorMedication): MedDraft {
  return {
    name: m.name,
    dosage: m.dosage ?? '',
    frequency: m.frequency ?? '',
    instructions: m.instructions ?? '',
    startDate: (m.startDate ?? '').slice(0, 10),
    endDate: (m.endDate ?? '').slice(0, 10),
    slots: [...(m.slots ?? [])],
  };
}

function draftToInput(d: MedDraft): MedicationInput {
  return {
    name: d.name,
    dosage: d.dosage,
    frequency: d.frequency || null,
    instructions: d.instructions,
    startDate: d.startDate || null,
    endDate: d.endDate || null,
    slots: d.slots,
  };
}

/** Only the fields that changed, so an edit never re-sends the name/dose. */
function changedFields(before: MedDraft, after: MedDraft): Partial<MedicationInput> {
  const full = draftToInput(after);
  const prev = draftToInput(before);
  const out: Partial<MedicationInput> = {};
  for (const k of Object.keys(full) as Array<keyof MedicationInput>) {
    const a = JSON.stringify(full[k] ?? null);
    const b = JSON.stringify(prev[k] ?? null);
    if (a !== b) (out as Record<string, unknown>)[k] = full[k];
  }
  return out;
}

type MedFormMode = { kind: 'add'; seed?: MedDraft } | { kind: 'edit'; med: EditorMedication };

export function MedsFeedingEditor({ apiUrl, dog, onClose, onChanged, footnote }: MedsFeedingEditorProps): JSX.Element {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [meds, setMeds] = useState<EditorMedication[]>(dog.medications.filter((m) => m.isActive));
  const [form, setForm] = useState<MedFormMode | null>(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') onClose();
  };

  const announce = useCallback(
    (msg: string) => {
      setStatus(msg);
      onChanged();
    },
    [onChanged],
  );

  return (
    <div className="ferro-mf-sheet" data-testid="meds-feeding-editor">
      <button type="button" className="ferro-mf-sheet__scrim" aria-label="Close meds and feeding" onClick={onClose} />
      <div
        ref={panelRef}
        className="ferro-mf-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="ferro-mf-sheet__header">
          <p className="ferro-mf-overline">Meds &amp; feeding</p>
          <h2 id={titleId} className="ferro-mf-sheet__title">
            {dog.name}
          </h2>
          {footnote ? <p className="ferro-mf-sheet__sub">{footnote}</p> : null}
        </header>

        <div className="ferro-mf-sheet__body">
          <section className="ferro-mf-section" aria-label="Medications">
            <h3 className="ferro-mf-section__title">Medications</h3>
            {meds.length === 0 && form?.kind !== 'add' ? (
              <p className="ferro-mf-empty" data-testid="meds-feeding-editor-empty">
                No medications yet. Add one, or keep using Medication notes.
              </p>
            ) : null}
            <ul className="ferro-mf-medlist">
              {meds.map((m) =>
                form?.kind === 'edit' && form.med.id === m.id ? (
                  <li key={m.id} className="ferro-mf-medlist__item">
                    <MedicationForm
                      apiUrl={apiUrl}
                      dogId={dog.id}
                      mode={form}
                      onCancel={() => setForm(null)}
                      onSaved={(saved) => {
                        setMeds((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
                        setForm(null);
                        announce(`Saved ${saved.name}.`);
                      }}
                      onStopAndReplace={(seed) => {
                        setMeds((prev) => prev.filter((p) => p.id !== m.id));
                        setForm({ kind: 'add', seed });
                        announce(`Stopped ${m.name}. Add the new dose below.`);
                      }}
                    />
                  </li>
                ) : (
                  <MedicationRow
                    key={m.id}
                    apiUrl={apiUrl}
                    dogId={dog.id}
                    med={m}
                    disabled={form !== null}
                    onEdit={() => setForm({ kind: 'edit', med: m })}
                    onGone={(why) => {
                      setMeds((prev) => prev.filter((p) => p.id !== m.id));
                      announce(why);
                    }}
                  />
                ),
              )}
            </ul>
            {form?.kind === 'add' ? (
              <MedicationForm
                apiUrl={apiUrl}
                dogId={dog.id}
                mode={form}
                onCancel={() => setForm(null)}
                onSaved={(saved) => {
                  setMeds((prev) => [...prev, saved]);
                  setForm(null);
                  announce(`Added ${saved.name}.`);
                }}
              />
            ) : (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-mf-btn"
                onClick={() => setForm({ kind: 'add' })}
                disabled={form !== null}
                data-testid="meds-feeding-add"
              >
                Add medication
              </button>
            )}
          </section>

          <MedicationNotesSection apiUrl={apiUrl} dog={dog} onSaved={() => announce('Medication notes saved.')} />
          <FeedingSection apiUrl={apiUrl} dog={dog} onSaved={() => announce('Feeding saved.')} />
        </div>

        <footer className="ferro-mf-sheet__footer">
          <p className="ferro-mf-sheet__status" role="status" aria-live="polite" data-testid="meds-feeding-status">
            {status}
          </p>
          <button
            type="button"
            className={`ferro-btn ${form === null ? 'ferro-btn--primary' : 'ferro-btn--outline'} ferro-mf-btn`}
            onClick={onClose}
            data-testid="meds-feeding-done"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── One medication, read mode ───────────────────────────────────────────

function MedicationRow({
  apiUrl,
  dogId,
  med,
  disabled,
  onEdit,
  onGone,
}: {
  apiUrl: string;
  dogId: string;
  med: EditorMedication;
  disabled: boolean;
  onEdit: () => void;
  onGone: (why: string) => void;
}): JSX.Element {
  const [busy, setBusy] = useState<null | 'stop' | 'delete'>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<{ text: string; offerStop: boolean } | null>(null);

  const stop = async () => {
    setBusy('stop');
    setError(null);
    const res = await stopMedication(apiUrl, dogId, med.id);
    setBusy(null);
    if (res.data) onGone(`Stopped ${med.name}. It is under Past medications on the profile.`);
    else setError({ text: res.error ?? "Couldn't stop it. Try again.", offerStop: false });
  };

  const remove = async () => {
    setBusy('delete');
    setError(null);
    const res = await deleteMedication(apiUrl, dogId, med.id);
    setBusy(null);
    setConfirmDelete(false);
    if (res.data) {
      onGone(`Deleted ${med.name}.`);
      return;
    }
    // 409: it has dose records — the server's sentence, and the honest way out.
    const hasDoses = /dose records/i.test(res.error ?? '');
    setError({ text: res.error ?? "Couldn't delete it. Try again.", offerStop: hasDoses });
  };

  const meta = [med.dosage, medTimesText(med), med.instructions].filter((p) => p && p.trim().length > 0).join(' · ');

  return (
    <li className="ferro-mf-medlist__item" data-testid={`meds-feeding-med-${med.id}`}>
      <div className="ferro-mf-medlist__head">
        <span className="ferro-mf-medlist__name">{med.name}</span>
        {meta ? <span className="ferro-mf-medlist__meta">{meta}</span> : null}
      </div>
      {confirmDelete ? (
        <div className="ferro-mf-confirm" role="group" aria-label={`Delete ${med.name}?`}>
          <p className="ferro-mf-confirm__text">
            Delete {med.name}? Only for a medication entered by mistake. To end one the dog was on, use Stop giving.
          </p>
          <div className="ferro-mf-actions">
            <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={() => setConfirmDelete(false)}>
              Keep it
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--danger ferro-mf-btn"
              onClick={remove}
              disabled={busy !== null}
              data-testid="meds-feeding-delete-confirm"
            >
              {busy === 'delete' ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      ) : (
        <div className="ferro-mf-actions">
          <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={onEdit} disabled={disabled || busy !== null}>
            Edit
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-mf-btn"
            onClick={stop}
            disabled={disabled || busy !== null}
            data-testid="meds-feeding-stop"
          >
            {busy === 'stop' ? 'Stopping…' : 'Stop giving'}
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-mf-btn"
            onClick={() => setConfirmDelete(true)}
            disabled={disabled || busy !== null}
            data-testid="meds-feeding-delete"
          >
            Delete (entered by mistake)
          </button>
        </div>
      )}
      {error ? (
        <div className="ferro-mf-error" role="alert" data-testid="meds-feeding-row-error">
          <p className="ferro-mf-error__text">{error.text}</p>
          {error.offerStop ? (
            <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={stop} disabled={busy !== null}>
              Stop giving instead
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

// ── Add / edit one medication ───────────────────────────────────────────

function MedicationForm({
  apiUrl,
  dogId,
  mode,
  onCancel,
  onSaved,
  onStopAndReplace,
}: {
  apiUrl: string;
  dogId: string;
  mode: MedFormMode;
  onCancel: () => void;
  onSaved: (m: EditorMedication) => void;
  onStopAndReplace?: (seed: MedDraft) => void;
}): JSX.Element {
  const initial = mode.kind === 'edit' ? draftFrom(mode.med) : (mode.seed ?? EMPTY_DRAFT);
  const [draft, setDraft] = useState<MedDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [banner, setBanner] = useState<{ text: string; offerReplace: boolean } | null>(null);
  const ids = useId();

  const set = <K extends keyof MedDraft>(k: K, v: MedDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleSlot = (s: string) =>
    setDraft((d) => ({ ...d, slots: d.slots.includes(s) ? d.slots.filter((x) => x !== s) : [...d.slots, s] }));
  const issue = (field: string) => issues.find((i) => i.field === field || i.field.startsWith(`${field}.`))?.message;

  const save = async () => {
    setSaving(true);
    setIssues([]);
    setBanner(null);
    const res =
      mode.kind === 'edit'
        ? await updateMedication(apiUrl, dogId, mode.med.id, changedFields(initial, draft))
        : await createMedication(apiUrl, dogId, draftToInput(draft));
    setSaving(false);
    if (res.data) {
      onSaved(res.data.medication);
      return;
    }
    const fields = fieldIssues(res.details);
    if (fields.length > 0) {
      setIssues(fields);
      return;
    }
    const hasDoses = /dose records/i.test(res.error ?? '');
    setBanner({
      text: res.error ?? "Couldn't save. Your changes are still here — try again.",
      offerReplace: hasDoses && mode.kind === 'edit',
    });
  };

  const stopAndReplace = async () => {
    if (mode.kind !== 'edit' || !onStopAndReplace) return;
    setSaving(true);
    const res = await stopMedication(apiUrl, dogId, mode.med.id);
    setSaving(false);
    if (res.data) onStopAndReplace({ ...draft, startDate: '', endDate: '' });
    else setBanner({ text: res.error ?? "Couldn't stop it. Try again.", offerReplace: false });
  };

  return (
    <form
      className="ferro-mf-form"
      data-testid="meds-feeding-med-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <fieldset className="ferro-mf-form__fields" disabled={saving}>
        <legend className="ferro-mf-form__legend">{mode.kind === 'edit' ? `Change ${mode.med.name}` : 'Add medication'}</legend>
        <Field id={`${ids}-name`} label="Name" error={issue('name')}>
          <input
            id={`${ids}-name`}
            className="ferro-mf-input"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            autoComplete="off"
            aria-invalid={issue('name') ? true : undefined}
            data-testid="meds-feeding-name"
          />
        </Field>
        <Field id={`${ids}-dose`} label="Dose" hint="e.g. 16 mg, one tablet" error={issue('dosage')}>
          <input
            id={`${ids}-dose`}
            className="ferro-mf-input"
            value={draft.dosage}
            onChange={(e) => set('dosage', e.target.value)}
            autoComplete="off"
            aria-invalid={issue('dosage') ? true : undefined}
            data-testid="meds-feeding-dose"
          />
        </Field>
        <div className="ferro-mf-field" role="group" aria-labelledby={`${ids}-times`}>
          <span id={`${ids}-times`} className="ferro-mf-label">
            Times
          </span>
          <div className="ferro-mf-chips">
            {SLOT_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                className="ferro-mf-chip"
                aria-pressed={draft.slots.includes(s)}
                onClick={() => toggleSlot(s)}
                data-testid={`meds-feeding-slot-${s}`}
              >
                {SLOT_SHORT[s]}
              </button>
            ))}
          </div>
          {issue('slots') ? <p className="ferro-mf-field__error">{issue('slots')}</p> : null}
        </div>
        <Field id={`${ids}-freq`} label="Frequency (optional)" hint="Kept for older records; the times above decide." error={issue('frequency')}>
          <select id={`${ids}-freq`} className="ferro-mf-input" value={draft.frequency} onChange={(e) => set('frequency', e.target.value)}>
            {FREQUENCIES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field id={`${ids}-instr`} label="Instructions" hint="e.g. with food" error={issue('instructions')}>
          <input
            id={`${ids}-instr`}
            className="ferro-mf-input"
            value={draft.instructions}
            onChange={(e) => set('instructions', e.target.value)}
            autoComplete="off"
          />
        </Field>
        <div className="ferro-mf-row">
          <Field id={`${ids}-start`} label="Start date" error={issue('startDate')}>
            <input id={`${ids}-start`} type="date" className="ferro-mf-input" value={draft.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </Field>
          <Field id={`${ids}-end`} label="End date" error={issue('endDate')}>
            <input id={`${ids}-end`} type="date" className="ferro-mf-input" value={draft.endDate} onChange={(e) => set('endDate', e.target.value)} />
          </Field>
        </div>
      </fieldset>
      {banner ? (
        <div className="ferro-mf-error" role="alert" data-testid="meds-feeding-form-error">
          <p className="ferro-mf-error__text">{banner.text}</p>
          {banner.offerReplace ? (
            <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={stopAndReplace} disabled={saving}>
              Stop giving and add new
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="ferro-mf-actions">
        <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="ferro-btn ferro-btn--primary ferro-mf-btn" disabled={saving} data-testid="meds-feeding-save-med">
          {saving ? 'Saving…' : mode.kind === 'edit' ? 'Save changes' : 'Save medication'}
        </button>
      </div>
    </form>
  );
}

// ── Medication notes (free text) ────────────────────────────────────────

function MedicationNotesSection({ apiUrl, dog, onSaved }: { apiUrl: string; dog: EditorDog; onSaved: () => void }): JSX.Element {
  const [value, setValue] = useState(dog.medicationNotes ?? '');
  const [saved, setSaved] = useState(dog.medicationNotes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();

  const save = async () => {
    setSaving(true);
    setError(null);
    const res = await updateFeeding(apiUrl, dog.id, { medicationNotes: value });
    setSaving(false);
    if (res.data) {
      setSaved(res.data.medicationNotes ?? '');
      setValue(res.data.medicationNotes ?? '');
      onSaved();
      return;
    }
    setError(fieldIssues(res.details)[0]?.message ?? res.error ?? "Couldn't save. Your changes are still here — try again.");
  };

  return (
    <section className="ferro-mf-section" aria-label="Medication notes">
      <h3 className="ferro-mf-section__title">Medication notes</h3>
      <Field id={id} label="Notes" hint="Anything that doesn't fit a medication row, like a rescue protocol." error={error ?? undefined}>
        <textarea
          id={id}
          className="ferro-mf-input ferro-mf-textarea"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          rows={3}
          data-testid="meds-feeding-med-notes"
        />
      </Field>
      <div className="ferro-mf-actions">
        <button
          type="button"
          className="ferro-btn ferro-btn--outline ferro-mf-btn"
          onClick={save}
          disabled={saving || value === saved}
          data-testid="meds-feeding-save-notes"
        >
          {saving ? 'Saving…' : 'Save notes'}
        </button>
      </div>
    </section>
  );
}

// ── Feeding ─────────────────────────────────────────────────────────────

function FeedingSection({ apiUrl, dog, onSaved }: { apiUrl: string; dog: EditorDog; onSaved: () => void }): JSX.Element {
  const start = {
    notes: dog.feedingNotes ?? '',
    foodType: dog.foodType ?? '',
    method: dog.feedingMethod ?? '',
    slots: [...(dog.feedingSlots ?? [])],
  };
  const [draft, setDraft] = useState(start);
  const [saved, setSaved] = useState(start);
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const ids = useId();
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const issue = (f: string) => issues.find((i) => i.field === f || i.field.startsWith(`${f}.`))?.message;

  const toggleMeal = (m: string) =>
    setDraft((d) => ({ ...d, slots: d.slots.includes(m) ? d.slots.filter((x) => x !== m) : [...d.slots, m] }));

  const save = async () => {
    setSaving(true);
    setIssues([]);
    setBanner(null);
    const res = await updateFeeding(apiUrl, dog.id, {
      feedingNotes: draft.notes,
      foodType: draft.foodType || null,
      feedingMethod: draft.method || null,
      feedingSlots: draft.slots,
    });
    setSaving(false);
    if (res.data) {
      const f = res.data.feeding;
      const next = {
        notes: f.notes ?? '',
        foodType: f.foodType ?? '',
        method: f.method ?? '',
        slots: [...(f.feedingSlots ?? draft.slots)],
      };
      setDraft(next);
      setSaved(next);
      onSaved();
      return;
    }
    const fields = fieldIssues(res.details);
    if (fields.length > 0) setIssues(fields);
    else setBanner(res.error ?? "Couldn't save. Your changes are still here — try again.");
  };

  return (
    <section className="ferro-mf-section" aria-label="Feeding">
      <h3 className="ferro-mf-section__title">Feeding</h3>
      <fieldset className="ferro-mf-form__fields" disabled={saving}>
        <Field id={`${ids}-notes`} label="Feeding notes" hint="What goes in the bowl, e.g. 1 cup kibble, warm water" error={issue('feedingNotes')}>
          <textarea
            id={`${ids}-notes`}
            className="ferro-mf-input ferro-mf-textarea"
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            rows={3}
            data-testid="meds-feeding-feeding-notes"
          />
        </Field>
        <div className="ferro-mf-row">
          <Field id={`${ids}-food`} label="Food type" error={issue('foodType')}>
            <select id={`${ids}-food`} className="ferro-mf-input" value={draft.foodType} onChange={(e) => setDraft((d) => ({ ...d, foodType: e.target.value }))}>
              {FOOD_TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field id={`${ids}-method`} label="Method" error={issue('feedingMethod')}>
            <select id={`${ids}-method`} className="ferro-mf-input" value={draft.method} onChange={(e) => setDraft((d) => ({ ...d, method: e.target.value }))}>
              {METHODS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="ferro-mf-field" role="group" aria-labelledby={`${ids}-meals`}>
          <span id={`${ids}-meals`} className="ferro-mf-label">
            Meal times
          </span>
          <div className="ferro-mf-chips">
            {MEAL_ORDER.map((m) => (
              <button
                key={m}
                type="button"
                className="ferro-mf-chip"
                aria-pressed={draft.slots.includes(m)}
                onClick={() => toggleMeal(m)}
                data-testid={`meds-feeding-meal-${m}`}
              >
                {MEAL_LABEL[m]}
              </button>
            ))}
          </div>
          <p className="ferro-mf-field__hint">
            Lunch is Noon, and also marks a daycare dog who eats lunch here. None picked: boarders eat Breakfast and
            Dinner.
          </p>
          {issue('feedingSlots') ? <p className="ferro-mf-field__error">{issue('feedingSlots')}</p> : null}
        </div>
      </fieldset>
      {banner ? (
        <div className="ferro-mf-error" role="alert">
          <p className="ferro-mf-error__text">{banner}</p>
        </div>
      ) : null}
      <div className="ferro-mf-actions">
        <button
          type="button"
          className="ferro-btn ferro-btn--outline ferro-mf-btn"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="meds-feeding-save-feeding"
        >
          {saving ? 'Saving…' : 'Save feeding'}
        </button>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="ferro-mf-field">
      <label htmlFor={id} className="ferro-mf-label">
        {label}
      </label>
      {children}
      {hint && !error ? <p className="ferro-mf-field__hint">{hint}</p> : null}
      {error ? (
        <p className="ferro-mf-field__error" data-testid={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
